import { I18n } from "@assets/strings";
import { List } from "immutable";
import debounce from "lodash/debounce";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { ClipLoader } from "react-spinners";
import styled from "styled-components";
import { useUserSearchWithExclusion } from "@domain/search/hook";
import { Person } from "@domain/user/user";
import { gap, mobile } from "@ui/styles/utils";
import { RoundedPlusButton } from "@ui/components/buttons/roundedPlusButton";
import { Card } from "@ui/components/containers/card";
import { DeleteIcon } from "@ui/components/icons";
import { InfiniteScroll } from "@ui/components/infiniteScroll";
import { row } from "@ui/components/layout";
import { UserView } from "@ui/components/profile/userView";
import { SearchBar } from "../search/searchBar";

interface UserSearchEngineProps extends React.HTMLAttributes<HTMLDivElement> {
	newUsers: List<Person>;
	onNewUsersChange: (newUsers: List<Person>, action?: "ADD" | "REMOVE") => void | Promise<void>;
	resultsMaxHeight?: number;
	selectionPosition?: "top" | "bottom";
	showOnlySearchResults?: boolean;
	groupId?: string;
	organizationId?: string;
}
export const UserSearchEngine: React.FC<UserSearchEngineProps> = ({
	newUsers,
	resultsMaxHeight,
	onNewUsersChange,
	selectionPosition = "top",
	showOnlySearchResults,
	groupId,
	organizationId,
	...htmlProps
}) => {
	const [searchValue, setSearchValue] = useState("");
	const [query, setQuery] = useState("");
	const { result: users, fetchMore, loading } = useUserSearchWithExclusion(query, groupId, organizationId);

	const debouncedChange = useCallback(
		debounce((v) => setQuery(v), 500, { leading: true, trailing: true }),
		[]
	);
	useEffect(() => () => debouncedChange.cancel(), []);
	const usersRef = useRef<HTMLDivElement>(null);

	const isChosen = (user: Person): boolean => !!newUsers.find((t) => t.id === user.id);

	const Selection = (
		<SelectedUsers>
			{newUsers.map((member, i) => (
				<SelectedUser key={member.id}>
					<UserView user={member} disabled />
					<DeleteIcon
						onClick={() => {
							onNewUsersChange(newUsers.remove(i), "REMOVE");
						}}
					/>
				</SelectedUser>
			))}
		</SelectedUsers>
	);

	return (
		<Container {...htmlProps}>
			{selectionPosition === "top" ? Selection : null}
			<SearchBar
				placeholder={I18n.t("members.search.placeholder")}
				value={searchValue}
				onChange={(v) => {
					setSearchValue(v);
					debouncedChange(v);
				}}
			/>
			{(showOnlySearchResults && users.length > 0 && searchValue.length > 0) ||
			(!showOnlySearchResults && users.length > 0) ? (
				<Users ref={usersRef} maxHeight={resultsMaxHeight}>
					<InfiniteScroll loading={loading} onEndReached={fetchMore} scrollingElement={usersRef}>
						{(users as Person[]).map((user) => (
							<UserWrapper key={user.id}>
								<UserView pictureSize={40} user={user as Person} disabled />
								<RoundedPlusButton
									inverted={isChosen(user)}
									onClick={() => {
										onNewUsersChange(
											isChosen(user) ? newUsers.remove(newUsers.indexOf(user)) : newUsers.push(user as Person),
											isChosen(user) ? "REMOVE" : "ADD"
										);
									}}
								/>
							</UserWrapper>
						))}
						{loading && <ClipLoader />}
					</InfiniteScroll>
				</Users>
			) : null}
			{selectionPosition === "bottom" ? Selection : null}
		</Container>
	);
};

const Container = styled.div`
	${gap(30)};
`;

const SelectedUsers = styled.div`
	${row(20)};
	flex-wrap: wrap;
`;

const Users = styled(Card).withConfig({
	shouldForwardProp: (prop) => prop !== "maxHeight",
})<{ maxHeight?: number }>`
	overflow: auto;
	border-radius: 6px;
	max-height: ${({ maxHeight = 300 }) => maxHeight}px;
`;

const UserWrapper = styled.div`
	padding: 20px 40px;
	&:not(:last-child) {
		border-bottom: 1px solid ${({ theme }) => theme.colors.lightergray};
	}
	${row(10, "space-between", "center")};

	@media ${mobile} {
		padding: 20px 5px;
	}
`;

const SelectedUser = styled(Card)`
	border-radius: 13px;
	padding: 4px 12px;
	margin-bottom: 10px;
	${row(10, "center", "center")};

	> svg  {
		cursor: pointer;
	}
`;
